import { Box, CircularProgress, CircularProgressLabel, Flex, Text } from "@chakra-ui/react";
import { uniqBy } from "lodash";
import { useTranslation } from "react-i18next";
import { BseDto, NegotiationInviteDto, OrganizationInviteDto } from "../../../../autogen/bff-api";
import { InvitedOrganizations } from "./InvitedOrganizations";
import { useNegotiation } from "./NegotiationContext";

interface Props {
  data: BseDto;
}

function countInvitesWithStatus<T extends { status: string }>(invites: T[], statuses: string[]): number {
  return invites.filter((invite) => statuses.includes(invite.status)).length;
}

export const Bids = ({ data }: Props) => {
  const { t } = useTranslation();
  const { currentRound } = useNegotiation();

  const invites = currentRound?.invites || data.organizationInvites;
  const emailInvites = currentRound ? [] : data.emailInvites; // Email invites are only in the original event

  const draftStatuses = ["DraftCreated", "BidIsSubmitted", "IsWinner", "IsLoser"];
  const draftCreated = countInvitesWithStatus<OrganizationInviteDto | NegotiationInviteDto>(invites, draftStatuses);

  const numberOfInvitedParties = uniqBy(emailInvites, (invite) => invite.organizationEntry?.id).length + invites.length;

  const deliveredBidStatuses = ["BidIsSubmitted", "IsWinner", "IsLoser"];
  const numberOfDeliveredBids = countInvitesWithStatus<OrganizationInviteDto | NegotiationInviteDto>(
    invites,
    deliveredBidStatuses
  );

  const declinedInvitations = countInvitesWithStatus<OrganizationInviteDto | NegotiationInviteDto>(invites, [
    "InvitationDeclined",
  ]);

  return (
    <>
      <Flex width="full" justifyContent="center" columnGap={{ base: 5, md: 20 }} pt="5">
        <Flex flexDirection="column" alignItems="center">
          <CircularProgress
            capIsRound
            value={(declinedInvitations / numberOfInvitedParties) * 100}
            size={32}
            color="smDanger"
            trackColor="smBackgroundSecondary"
          >
            <CircularProgressLabel fontSize="lg">{`${declinedInvitations} ${t(
              "of"
            )} ${numberOfInvitedParties}`}</CircularProgressLabel>
          </CircularProgress>
          <Text size="sm" fontWeight="semibold">
            {t("Declined")}
          </Text>
        </Flex>
        <Flex flexDirection="column" alignItems="center">
          <CircularProgress
            capIsRound
            value={(draftCreated / numberOfInvitedParties) * 100}
            size={32}
            color="smPrimary"
            trackColor="smBackgroundSecondary"
          >
            <CircularProgressLabel fontSize="lg">{`${draftCreated} ${t(
              "of"
            )} ${numberOfInvitedParties}`}</CircularProgressLabel>
          </CircularProgress>
          <Text size="sm" fontWeight="semibold">
            {t("Draft created")}
          </Text>
        </Flex>
        <Flex flexDirection="column" alignItems="center">
          <CircularProgress
            capIsRound
            value={(numberOfDeliveredBids / numberOfInvitedParties) * 100}
            size={32}
            color="smTertiary"
            trackColor="smBackgroundSecondary"
          >
            <CircularProgressLabel fontSize="lg">
              {`${numberOfDeliveredBids} ${t("of")} ${numberOfInvitedParties}`}
            </CircularProgressLabel>
          </CircularProgress>
          <Text size="sm" fontWeight="semibold">
            {t("Bid delivered")}
          </Text>
        </Flex>
      </Flex>
      <Box display={"flex"} borderRadius={"md"} pt="10" flexDirection={"column"}>
        <Text fontSize={"lg"} fontWeight="semibold">
          {t("Invited companies & bids")}
        </Text>
        <Box h="15px"></Box>
        <InvitedOrganizations event={data} />
      </Box>
    </>
  );
};
